/**
 * NutzungsordnungModal — Compliance-Gate vor der ersten Poolfahrzeug-Nutzung (Issue #163).
 *
 * Öffnet sich automatisch (über `open`-Prop), wenn der Mitarbeiter für die
 * aktuelle Version noch keine Bestätigung hinterlegt hat. Der „Bestätigen"-
 * Button ist bewusst nur aktiv, wenn:
 *   1. der Text bis zum Ende gescrollt wurde (Compliance-Nachweis), UND
 *   2. die Checkbox „Ich habe gelesen und stimme zu" gesetzt ist.
 *
 * Nach POST an /api/nutzungsordnung wird der `onConfirmed`-Callback ausgelöst;
 * das Parent-Component schliesst dann das Modal und gibt die UI-Aktionen frei.
 *
 * Der Text und die Versionsnummer kommen aus `shared/nutzungsordnung` —
 * Server (assertNutzungsordnungBestaetigt) und Client zeigen dadurch
 * garantiert dieselbe Fassung.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  NUTZUNGSORDNUNG_TEXT,
  NUTZUNGSORDNUNG_VERSION,
} from '@shared/nutzungsordnung';

export interface NutzungsordnungModalProps {
  open: boolean;
  mitarbeiterId: string;
  fahrzeugId: string;
  fahrzeugBezeichnung?: string;
  onConfirmed: () => void;
  onCancel: () => void;
}

// 12 px Toleranz — viele Browser runden scrollTop/scrollHeight minimal ab, so
// dass strict-equal nie true würde. Mobile-Safari liefert 1–2 px Bounce-Back.
const SCROLL_END_TOLERANCE_PX = 12;

export default function NutzungsordnungModal({
  open,
  mitarbeiterId,
  fahrzeugId,
  fahrzeugBezeichnung,
  onConfirmed,
  onCancel,
}: NutzungsordnungModalProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [gelesenCheckbox, setGelesenCheckbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setScrolledToEnd(false);
      setGelesenCheckbox(false);
      setSubmitting(false);
    }
  }, [open]);

  // Auf Viewports mit viel Platz kann der Text bereits komplett sichtbar sein —
  // dann erreicht der Nutzer das Ende ohne zu scrollen. In dem Fall geben wir
  // das Gate direkt frei, damit der Bestätigen-Button aktivierbar ist.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.clientHeight <= SCROLL_END_TOLERANCE_PX) {
      setScrolledToEnd(true);
    }
  }, [open]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= SCROLL_END_TOLERANCE_PX) setScrolledToEnd(true);
  };

  const canConfirm = scrolledToEnd && gelesenCheckbox && !submitting;

  const handleSubmit = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const response = await fetch('/api/nutzungsordnung', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mitarbeiter_id: mitarbeiterId,
          fahrzeug_id: fahrzeugId,
          bestaetigt_per: 'ui_klick',
        }),
      });

      // 409 = bereits bestätigt (Unique-Constraint) → aus UI-Sicht Erfolg,
      // der Mitarbeiter ist schon freigeschaltet.
      if (response.ok || response.status === 409) {
        toast.success('Nutzungsordnung bestätigt');
        onConfirmed();
        return;
      }

      const body = (await response.json().catch(() => null)) as { error?: string } | null;
      toast.error(body?.error ?? 'Bestätigung konnte nicht gespeichert werden');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Netzwerkfehler';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nutzungsordnung-title"
      data-testid="nutzungsordnung-modal"
    >
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl mx-0 sm:mx-4 h-[85vh] sm:h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 id="nutzungsordnung-title" className="text-sm font-bold text-gray-900">
              Nutzungsordnung Poolfahrzeug
            </h3>
            <p className="text-[10px] text-gray-400 mt-0.5">
              Version {NUTZUNGSORDNUNG_VERSION}
              {fahrzeugBezeichnung ? ` · ${fahrzeugBezeichnung}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Abbrechen"
            className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400" />
          </button>
        </div>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          data-testid="nutzungsordnung-scroll"
          className="flex-1 min-h-0 overflow-auto px-4 sm:px-6 py-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
        >
          {NUTZUNGSORDNUNG_TEXT}
        </div>

        <div className="border-t border-gray-100 px-4 sm:px-5 py-3 sm:py-4 space-y-3 shrink-0">
          <label
            className={`flex items-start gap-2 text-sm select-none ${
              scrolledToEnd ? 'text-gray-800 cursor-pointer' : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <input
              type="checkbox"
              disabled={!scrolledToEnd}
              checked={gelesenCheckbox}
              onChange={e => setGelesenCheckbox(e.target.checked)}
              data-testid="nutzungsordnung-checkbox"
              className="mt-1"
            />
            <span>
              Ich habe die Nutzungsordnung gelesen und stimme zu.
              {!scrolledToEnd && (
                <span className="block text-[11px] text-gray-400 mt-0.5">
                  Bitte zuerst bis zum Ende scrollen.
                </span>
              )}
            </span>
          </label>

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-40"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canConfirm}
              data-testid="nutzungsordnung-confirm"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <Check className="w-4 h-4" />
              {submitting ? 'Speichere…' : 'Bestätigen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
